import {ApiError} from '@/api/meta/error';
import {authorizeApplication, authorizeUser, getCustomers} from '@/api/module/misc';
import {getClients, getLicences, getSessionInfo} from '@/api/module/settings';
import {ApiApplicationToken} from '@/model/ApplicationToken';
import {ApiClient} from '@/model/Client';
import {ApiCustomer} from '@/model/Customer';
import {ApiSessionInfo} from '@/model/SessionInfo';
import {ApiUserToken} from '@/model/UserToken';
import {ApiWarehouse} from '@/model/Warehouse';
import {Preferences} from '@capacitor/preferences';
import moment from 'moment';
import {defineStore} from 'pinia';
import {computed, reactive, ref, watch} from 'vue';
import {useApplicationState} from './applicationState';
import {doAlert} from '@/util';
import router from '@/router';
import {Device} from '@capacitor/device';

export const settingsNames: Record<keyof Settings, string> = {
  selectedWarehouse: 'Lagerhalle',
  selectedSubsidiary: 'Niederlassung',
  userToken: 'Benutzertoken',
  applicationToken: 'Anwendungstoken',
  userShort: 'Benutzerkürzel',
  customerId: 'Kunde',
  client: 'Mandant',
  licenses: 'Lizenzen',
  username: 'Benutzername',
};

interface GeneralSettings {
  userToken: ApiUserToken | null;
  applicationToken: ApiApplicationToken | null;
  userShort: string | null;
  customerId: string | null;
  client: number | null;
  licenses: Record<string, boolean>;
  username: string | null;
}

interface SettingsState extends GeneralSettings {
  selectedSubsidiaries: Record<string, number>;
  selectedWarehouses: Record<string, Record<number, ApiWarehouse>>;
}

export interface Settings extends GeneralSettings {
  selectedSubsidiary: number | null,
  selectedWarehouse: ApiWarehouse | null,
}

function defaultState(): SettingsState {
  return {
    userToken: null,
    username: null,
    applicationToken: null,
    userShort: null,
    customerId: null,
    client: null,
    selectedSubsidiaries: {},
    selectedWarehouses: {},
    licenses: {},
  };
}

export const useSettings = defineStore('settingsV2', () => {
  const savedState = reactive(defaultState());
  const settingsLoaded = ref(false);

  const customers = ref<ApiCustomer[]>([]);
  const customersLoaded = ref(false);
  const clients = ref<Record<string, ApiClient[]>>({});
  const clientsLoaded = ref<Record<string, true>>({});
  const sessionDatas = ref<Record<string, ApiSessionInfo>>({});
  const sessionDatasLoaded = ref<Record<string, true>>({});
  const ready = ref(false);

  const licensesError = ref(null as Error | null);
  const licensesLoading = ref(false);

  const customersError = ref(null as Error | null);
  const customersLoading = ref(false);

  async function waitReady() {
    return new Promise<void>((res) => watch(ready, (val) => {
      if (val) res();
    }, {immediate: true}));
  }

  async function waitLoggedIn() {
    return new Promise<void>((res) => watch(() => savedState.userToken, (val) => {
      if (val) res();
    }));
  }

  // Map state (somehow necessary for this store to work)
  function computedState<K extends keyof SettingsState>(key: K) {
    return computed<SettingsState[K]>({
      get: () => savedState[key],
      set: (newValue) => savedState[key] = newValue,
    });
  }

  function computedReadonlyState<K extends keyof SettingsState>(key: K) {
    return computed(() => savedState[key]);
  }

  const userToken = computedReadonlyState('userToken');
  const applicationToken = computedReadonlyState('applicationToken');
  const customerId = computedState('customerId');
  const client = computedState('client');
  const licenses = computedReadonlyState('licenses');
  const username = computedReadonlyState('username');


  // Computed state
  const selectedSubsidiary = computed({
    get: () => savedState.client ? savedState.selectedSubsidiaries[savedState.client] ?? null : null,
    set: (newSubsidary) => {
      if (savedState.client === null) return;
      if (newSubsidary !== null) {
        savedState.selectedSubsidiaries[savedState.client] = newSubsidary;
      } else {
        delete savedState.selectedSubsidiaries[savedState.client];
      }
      savedState.selectedSubsidiaries = {...savedState.selectedSubsidiaries};
    },
  });

  const selectedWarehouse = computed({
    get: () => savedState.client !== null && selectedSubsidiary.value !== null ?
      savedState.selectedWarehouses[savedState.client]?.[selectedSubsidiary.value] ?? null :
      null,
    set: (newWarehouse) => {
      if (savedState.client === null || selectedSubsidiary.value === null) return;
      if (!savedState.selectedWarehouses[savedState.client]) savedState.selectedWarehouses[savedState.client] = {};
      if (newWarehouse !== null) {
        savedState.selectedWarehouses[savedState.client][selectedSubsidiary.value] = newWarehouse;
      } else {
        delete savedState.selectedWarehouses[savedState.client][selectedSubsidiary.value];
      }
      savedState.selectedWarehouses = {...savedState.selectedWarehouses};
    },
  });

  const sessionData = computed(() => savedState.customerId ? sessionDatas.value[savedState.customerId] ?? null : null);

  const userShort = computed({
    get: () => sessionData.value?.Bediener.Kuerzel ?? savedState.userShort,
    set: (newUserShort) => {
      if (sessionData.value?.Bediener.Kuerzel) return;

      savedState.userShort = newUserShort;
    },
  });

  watch(() => savedState.userToken, (newUserToken) => {
    if (!ready.value) return;
    useApplicationState().validate();
    if (newUserToken !== null) {
      fetchLicences();
      return;
    }
    savedState.customerId = null;
    savedState.licenses = {};
    sessionDatas.value = {};
    return;
  });

  watch(() => savedState.customerId, (newCustomerId) => {
    if (!ready.value) return;
    if (newCustomerId === null) {
      savedState.client = null;
      return;
    }

    if (!customers.value.some((customer) => customer.CustomerId === newCustomerId)) {
      savedState.customerId = customers.value.find(() => true)?.CustomerId ?? null;
    }
    savedState.licenses = {};
    fetchLicences();
  });

  // Save the state
  let lastSavedState: typeof savedState = structuredClone(savedState);
  watch(savedState, async (newState) => {
    if (!ready.value) return;
    // eslint-disable-next-line guard-for-in
    for (const stateKey in newState) {
      const key = stateKey as keyof typeof lastSavedState;
      if (lastSavedState[key] === newState[key]) continue;
      await Preferences.set({
        key,
        value: JSON.stringify(savedState[key]),
      });
    }
    lastSavedState = structuredClone(newState);
  }, {deep: true});

  async function init() {
    await load();
    if (!await getUserToken()) return;
    fetchLicences().catch(console.error);
    fetchCustomers().catch(console.error);
  }

  // Loading the state
  async function load() {
    const state = defaultState();
    // eslint-disable-next-line guard-for-in
    for (const stateKey in state) {
      const key = stateKey as keyof typeof state;
      const {value} = await Preferences.get({
        key,
      });
      if (value === null) continue;

      // @ts-expect-error this works
      savedState[key] = JSON.parse(value);
    }

    settingsLoaded.value = true;
    ready.value = true;
  }

  async function fetchCustomers(bearerToken?: string) {
    try {
      customersLoading.value = true;
      const customers = await loadCustomers();
      for (const customer of customers) {
        console.log(`Mandanten laden ${customer.CustomerId}: `, customer.CustomerName);
        const clients = await loadClients(customer.CustomerId, bearerToken);
        console.log(`Mandanten ${customer.CustomerId}: `, clients);
        if (!clients.length) continue;
        try {
          await loadSessionData(customer.CustomerId, bearerToken, clients.find(() => true)!.MandantNr);
        } catch (_) {
        }
      }
    } catch (err) {
      customersError.value = err as Error;
    } finally {
      customersLoading.value = false;
    }
  }

  async function fetchLicences() {
    try {
      licensesLoading.value = true;
      const [allLicenses] = await getLicences();
      savedState.licenses = Object.fromEntries(allLicenses.map((license) => [license.License.IdentifierName, license.LicenseCount > 0]));
      licensesError.value = null;
    } catch (err) {
      licensesError.value = err as Error;
    } finally {
      licensesLoading.value = false;
    }
  }

  async function loadCustomers(bearerToken?: string) {
    if (customersLoaded.value) return customers.value;
    [customers.value] = await getCustomers(bearerToken);
    customersLoaded.value = true;

    return customers.value;
  }

  async function loadClients(customerId: string, bearerToken?: string) {
    if (clientsLoaded.value[customerId]) return clients.value[customerId];
    [clients.value[customerId]] = await getClients({customerId}, bearerToken);
    clientsLoaded.value[customerId] = true;

    return clients.value[customerId];
  }

  async function loadSessionData(customerId?: string, bearerToken?: string, client?: number): Promise<ApiSessionInfo | null> {
    if (customerId) {
      if (sessionDatasLoaded.value[customerId]) return sessionDatas.value[customerId];
    }

    let sessionData: ApiSessionInfo | null = null;

    sessionData = await getSessionInfo(customerId, bearerToken, client);

    if (customerId) {
      console.log('set loaded');
      if (sessionData) sessionDatas.value[customerId] = sessionData;
      sessionDatasLoaded.value[customerId] = true;

      return sessionDatas.value[customerId] ?? null;
    } else {
      return sessionData;
    }
  }

  async function firstAvailableCustomerId(bearerToken?: string): Promise<string | null> {
    try {
      customersLoading.value = true;

      const customers = await loadCustomers(bearerToken);


      for (const customer of customers) {
        const clients = await loadClients(customer.CustomerId, bearerToken);
        if (!clients.length) continue; // no clients in this customer
        try {
          const sessionData = await loadSessionData(customer.CustomerId, bearerToken, clients.find(() => true)!.MandantNr);
          if (sessionData?.Bediener.Typ === 'Kunde') continue;
          return customer.CustomerId;
        } catch (_) {
          continue;
        }
      }
    } catch (err) {
      customersError.value = err as Error;
    } finally {
      customersLoading.value = false;
    }

    return null;
  }

  async function login(Username: string, Password: string) {
    try {
      const applicationToken = await getApplicationToken();
      const response = await authorizeUser({
        Username,
        Password,
        ApplicationToken: applicationToken.Token,
      }).catch((err) => {
        if (err instanceof ApiError) {
          if ((err as ApiError).code === 401) {
            doAlert('Benutzername oder Passwort falsch');
          }
        }
        return;
      });

      if (!response) {
        return;
      }

      const [userToken] = response;

      const customerId = await firstAvailableCustomerId(userToken.Token);

      if (!customerId) throw new Error('Anmeldung für externe User nicht erlaubt.');

      const clients = await loadClients(customerId, userToken.Token);

      const client = clients.find(() => true)?.MandantNr ?? null;

      [savedState.userToken, savedState.customerId, savedState.client, savedState.username] = [
        userToken, customerId, client, Username,
      ];
    } catch (err) {
      customers.value = [];
      sessionDatas.value = {};
      savedState.licenses = {};
      throw err;
    }
  }

  async function logout() {
    savedState.userToken = null;
    router.replace('/login').then(async () => {
      await Preferences.clear();
      console.log('logged out');
      router.go(0);
    });
  }

  async function getApplicationToken(allowBeforeReady = false) {
    if (!allowBeforeReady) await waitReady();
    if (!savedState.applicationToken || moment(savedState.applicationToken.ValidTill).isBefore()) {
      [savedState.applicationToken] = await authorizeApplication();
    }
    return savedState.applicationToken;
  }

  async function getUserToken(allowBeforeReady = false, waitForLogin = false) {
    if (!allowBeforeReady) await waitReady();
    if (waitForLogin) await waitLoggedIn();
    if (!savedState.userToken) return null;
    const userTokenValidTill = moment(savedState.userToken?.TokenValidTill);

    if (userTokenValidTill.isBefore()) {
      savedState.userToken = null;
    }

    return savedState.userToken;
  }

  return {
    sessionDatas,
    sessionDatasLoaded,
    savedState,
    userToken,
    applicationToken,
    sessionData,
    customerId,
    client,
    licenses,
    userShort,
    selectedSubsidiary,
    selectedWarehouse,
    customers,
    username,
    load,
    init,
    licensesError,
    licensesLoading,
    customersError,
    customersLoading,
    ready,
    login,
    logout,
    getApplicationToken,
    getUserToken,
    fetchLicences,
    fetchCustomers,
    waitReady,
    waitLoggedIn,
  };
});
